import React from 'react';
import { ConfirmDialog, OverFullWindow } from 'components/UI';
import { MultiStepForm } from 'components/UI/V3/Forms/MultiStepForm/MultiStepForm';
import { MembershipDetailsStep } from 'components/Memberships/Add/Steps/Details/MembershipDetailsStep';
import {
    Membership,
    MembershipBillingFrequency,
    MembershipEnrollmentType,
    MembershipItem,
    MembershipItemType,
    MembershipRolloverUsage,
    MembershipPackageExpiration,
    MembershipPackageExpirationUnit,
    MEMBERSHIP_ITEM_TYPES,
    mapItemTypeToBackend
} from '@spike/membership-model';
import { MembershipTypeStep } from 'components/Memberships/Add/Steps/Type/MembershipTypeStep';
import Button from 'components/UI/V2/Button/Button';
import { MembershipItemsStep } from 'components/Memberships/Add/Steps/Items/MembershipItemsStep';
import { useDispatch, useSelector } from 'react-redux';
import { fetchServicesThunk } from 'actions/services/ServicesActions';
import { fetchProductsThunk } from '@spike/products-action';
import { fetchThunk as fetchAddOnServicesThunk } from '@spike/addon-services-action';
import { useApiClientWrapper } from 'hooks';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { MembershipSuccess } from 'components/Memberships/Add/Steps/Success/MembershipSuccess';
import useNonInitialEffect from '@versiondos/hooks';
import { RootState } from 'store';
import { MembershipsStatus, addMembershipThunk, updateMembershipThunk } from '@spike/memberships-action';

interface AddMembershipProps {
    onClose: () => void;
    editMembership?: Membership;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: 920,
            margin: '0px auto',
            padding: '32px 0px',

            [theme.breakpoints.up('md')]: {
                padding: '60px 0px'
            }
        },
        confirmDialogWarningText: {
            maxWidth: 'unset',
            margin: '0px auto',
            paddingLeft: '12px',
            paddingRight: '12px',
            [theme.breakpoints.up('md')]: {
                paddingLeft: 0,
                paddingRight: 0
            }
        }
    })
);

export const AddMembership: React.FC<AddMembershipProps> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const apiClient = useApiClientWrapper();
    const apiClientWrapper = useApiClientWrapper();
    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);

    const isEditMode = !!props.editMembership;
    const originalItemsRef = React.useRef<MembershipItem[]>(props.editMembership?.membershipItems ?? []);

    useNonInitialEffect(() => {
        switch (membershipsStatus) {
            case MembershipsStatus.AddSuccess:
                setIsSuccess(true);
                break;
            case MembershipsStatus.UpdateSuccess:
                props.onClose();
                break;
        }
    }, [membershipsStatus]);

    const [formStep, setFormStep] = React.useState(0);
    const [isSuccess, setIsSuccess] = React.useState(false);
    const [showSubscribersWarning, setShowSubscribersWarning] = React.useState(false);
    const pendingSubmitRef = React.useRef<{ items: MembershipItem[]; total: string } | null>(null);
    const [membership, setMembership] = React.useState<Membership>(
        props.editMembership ?? {
            marketplaceID: apiClientWrapper.marketplaceId,
            name: '',
            description: '',
            enrollmentType: '' as MembershipEnrollmentType,
            rolloverUsage: '' as MembershipRolloverUsage,
            rolloverBillingCycles: null,
            billingFrequency: '' as MembershipBillingFrequency,
            billingIntervalDays: null,
            packageExpiration: null,
            packageExpirationValue: null,
            packageExpirationUnit: null,
            onlineBooking: false,
            active: true,
            deleted: false,
            createdAt: '',
            updatedAt: '',
            total: undefined,
            membershipItems: []
        }
    );
    const [showExitPrompt, setShowExitPrompt] = React.useState(false);

    const nextStepHandler = () => setFormStep(formStep + 1);
    const previousStepHandler = () => setFormStep(formStep - 1);

    const editStepHandler = (step: number) => {
        setFormStep(step);
    };

    const membershipDetailsHandler = (name: string, description: string, bookableOnline: boolean) => {
        setMembership({
            ...membership,
            name,
            description,
            onlineBooking: bookableOnline
        });

        nextStepHandler();
    };

    const membershipTypeHandler = (
        type: MembershipEnrollmentType,
        billingFrequency: MembershipBillingFrequency | '',
        billingIntervalDays: number | null,
        rolloverUsage: MembershipRolloverUsage,
        rolloverBillingCycles: Membership['rolloverBillingCycles'],
        packageExpiration: MembershipPackageExpiration,
        packageExpirationValue: number | null,
        packageExpirationUnit: MembershipPackageExpirationUnit | null
    ) => {
        setMembership({
            ...membership,
            enrollmentType: type,
            billingFrequency:
                type === MembershipEnrollmentType.SUBSCRIPTION ? billingFrequency : ('' as MembershipBillingFrequency),
            billingIntervalDays:
                type === MembershipEnrollmentType.SUBSCRIPTION && billingFrequency === MembershipBillingFrequency.CUSTOM
                    ? billingIntervalDays
                    : null,
            rolloverUsage:
                type === MembershipEnrollmentType.SUBSCRIPTION ? rolloverUsage : MembershipRolloverUsage.RESET,
            rolloverBillingCycles: type === MembershipEnrollmentType.SUBSCRIPTION ? rolloverBillingCycles : null,
            packageExpiration: type === MembershipEnrollmentType.PACKAGE ? packageExpiration : null,
            packageExpirationValue: type === MembershipEnrollmentType.PACKAGE ? packageExpirationValue : null,
            packageExpirationUnit: type === MembershipEnrollmentType.PACKAGE ? packageExpirationUnit : null
        });

        nextStepHandler();
    };

    const membershipItemsHandler = async (items: MembershipItem[], total: string) => {
        const itemsForBackend = items.map(item => ({
            ...item,
            itemType: mapItemTypeToBackend(item.itemType)
        }));

        const finalMembership: Membership = {
            ...membership,
            membershipItems: itemsForBackend,
            total,
            marketplaceID: apiClientWrapper.marketplaceId
        };

        if (isEditMode && props.editMembership?.id) {
            if ((props.editMembership.subscribedCount ?? 0) > 0) {
                setMembership(finalMembership);
                pendingSubmitRef.current = { items: itemsForBackend, total };
                setShowSubscribersWarning(true);
                return;
            }
            await dispatch(
                updateMembershipThunk(apiClient, props.editMembership.id, finalMembership, originalItemsRef.current)
            );
        } else {
            await dispatch(addMembershipThunk(apiClient, finalMembership));
        }
    };

    const handleConfirmSubscribersWarning = async () => {
        setShowSubscribersWarning(false);
        if (!pendingSubmitRef.current || !props.editMembership?.id) return;

        const { items, total } = pendingSubmitRef.current;
        pendingSubmitRef.current = null;

        await dispatch(
            updateMembershipThunk(
                apiClient,
                props.editMembership.id,
                { ...membership, membershipItems: items, total },
                originalItemsRef.current
            )
        );
    };

    React.useEffect(() => {
        // Fetch services by business area
        dispatch(
            fetchServicesThunk(undefined, undefined, undefined, 'active', undefined, undefined, 'grooming,dental')
        );
        dispatch(fetchServicesThunk(undefined, undefined, undefined, 'active', undefined, undefined, 'daycare'));
        dispatch(fetchServicesThunk(undefined, undefined, undefined, 'active', undefined, undefined, 'boarding'));
        dispatch(fetchServicesThunk(undefined, undefined, undefined, 'active', undefined, undefined, 'walking'));
        dispatch(fetchServicesThunk(undefined, undefined, undefined, 'active', undefined, undefined, 'transportation'));
        dispatch(fetchServicesThunk(undefined, undefined, undefined, 'active', undefined, undefined, 'training'));
        dispatch(fetchProductsThunk(apiClient, '', [], [apiClient.marketplaceId], [], 1, 10000));
        dispatch(fetchAddOnServicesThunk(apiClient));
    }, []);

    if (isSuccess) {
        return (
            <OverFullWindow
                showMobileLogo
                title="Adding a Membership"
                onClose={props.onClose}
            >
                <MembershipSuccess
                    membershipName={membership.name}
                    onClose={props.onClose}
                />
            </OverFullWindow>
        );
    }

    return (
        <OverFullWindow
            showMobileLogo
            title={isEditMode ? 'Editing Membership' : 'Adding a Membership'}
            onClose={() => setShowExitPrompt(true)}
        >
            <ConfirmDialog
                open={showExitPrompt}
                title="Exit Without Saving?"
                onCancel={() => setShowExitPrompt(false)}
                question="Your unsaved changes will be lost."
                actions={
                    <>
                        <Button
                            fullWidth
                            size="large"
                            variant="danger"
                            label="Discard"
                            style={{ minWidth: 'unset' }}
                            onClick={props.onClose}
                        />

                        <Button
                            fullWidth
                            size="large"
                            variant="outlined"
                            label="Keep Editing"
                            style={{ margin: 0, minWidth: 'unset' }}
                            onClick={() => setShowExitPrompt(false)}
                        />
                    </>
                }
            />

            {isEditMode && (
                <ConfirmDialog
                    open={showSubscribersWarning}
                    title="Changes Apply to Active Subscribers"
                    onCancel={() => setShowSubscribersWarning(false)}
                    question={
                        <Typography
                            component="div"
                            className={classes.confirmDialogWarningText}
                        >
                            This membership has{' '}
                            <b style={{ fontWeight: 600 }}>
                                {props.editMembership?.subscribedCount} active subscribers
                            </b>
                            .
                            <br />
                            Any changes you make — including pricing, included items, discounts, billing settings, or
                            expiration rules — will apply immediately to all of them.
                            <br />
                            <br />
                            Upcoming renewal charges may be affected.
                            <br />
                            This action cannot be undone.
                        </Typography>
                    }
                    actions={
                        <>
                            <Button
                                fullWidth
                                size="large"
                                variant="black"
                                label="Cancel"
                                style={{ minWidth: 'unset' }}
                                onClick={() => setShowSubscribersWarning(false)}
                            />

                            <Button
                                fullWidth
                                size="large"
                                variant="green"
                                label="Save Changes"
                                style={{ margin: 0, minWidth: 'unset' }}
                                onClick={handleConfirmSubscribersWarning}
                            />
                        </>
                    }
                />
            )}

            <div className={classes.container}>
                <MultiStepForm
                    steps={[
                        {
                            title: 'Membership details',
                            component: (
                                <MembershipDetailsStep
                                    membership={membership}
                                    onNext={membershipDetailsHandler}
                                />
                            )
                        },
                        {
                            title: 'Membership Type',
                            component: (
                                <MembershipTypeStep
                                    membership={membership}
                                    onNext={membershipTypeHandler}
                                />
                            )
                        },
                        {
                            title: 'Items & Pricing',
                            tooltip: (
                                <>
                                    Taxes will be applied to any
                                    <br />
                                    items marked as taxable.
                                </>
                            ),
                            component: (
                                <MembershipItemsStep
                                    membership={membership}
                                    onNext={membershipItemsHandler}
                                    marketplaceID={apiClientWrapper.marketplaceId}
                                />
                            )
                        }
                    ]}
                    activeStep={formStep}
                    onEdit={editStepHandler}
                    flexibleNavigation={isEditMode}
                />
            </div>
        </OverFullWindow>
    );
};
